'use client';

import LinkIcon from '@mui/icons-material/Link';
import { Button, Popover, Stack, TextField, Theme, Typography } from '@mui/material';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { detectClipboardAvailability, TClipboardAvailability } from './detectClipboardAvailability';

export type { TClipboardAvailability };

/**
 * The control it replaces is `Show REQUEST LINK` in `LivenessPanel`: an outlined, intrinsic-width
 * button that opened a dialog carrying the URL, a QR code and a download link. It was agreed to be
 * demoted rather than removed, because most clients want the system to send the email and the few
 * who send their own still need the link.
 *
 * So this is a small text button on a line of its own, and it copies rather than opening anything.
 * A control that opened the same dialog would keep all of the real estate and merely move it one
 * click later, which is not a demotion.
 *
 * **Capability first, with a fallback reachable two ways.** Two different things can go wrong and
 * they are caught at different moments, which is worth being precise about:
 *
 * - The browser can report no clipboard at all, which is what an insecure context looks like.
 *   `detectClipboardAvailability` catches that up front, before any click side effect.
 * - The browser can report a clipboard and then refuse the write, which is what a denied permission
 *   or an unfocused document looks like, and is the shape some embedded browsers take. Nothing can
 *   detect that ahead of time, so it surfaces only as a rejected promise from `writeText`.
 *
 * Both land on the same popover. The user's need is identical either way, so distinguishing them on
 * screen would ask them to care about something that does not change what they do next.
 */
export type TRequestLinkActionProps = {
  /** The link a client sends their customer. */
  url: string;
  /**
   * Story seam only. The shipping control detects clipboard capability itself, once per click, from
   * the browser; this override exists purely so a Storybook story can force either visual state
   * without needing a second browser. Do not set it from application code.
   */
  availabilityOverride?: TClipboardAvailability;
  /**
   * Called when the clipboard write rejects, after the fallback has already been shown.
   *
   * The user is never stranded by a failed write, so this is not an error path they see. It exists
   * because this package has no logging surface of its own, and without it a clipboard denial is
   * completely unobservable: no count of how often the fallback is reached, and nothing to consult
   * when someone reports the popover appearing unexpectedly. The calling app owns the logger, so it
   * is the only place that can record this with any context.
   */
  onCopyError?: (error: unknown) => void;
};

/** How long the `Copied` acknowledgement stays up, in milliseconds. */
const COPIED_CUE_DURATION = 2000;

export default function RequestLinkAction({
  url,
  availabilityOverride,
  onCopyError,
}: Readonly<TRequestLinkActionProps>): ReactElement {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const cueTimer = useRef<ReturnType<typeof setTimeout>>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // Tracked rather than fired and forgotten. Two clicks inside the cue window would otherwise leave
  // two timers running, and the first to fire clears the cue while the second click's window is
  // still open, so the acknowledgement disappears early. Clearing on unmount is the same fix: React
  // discards the late `setHasCopied` silently, but the timer itself outlives the component.
  useEffect(() => () => clearTimeout(cueTimer.current), []);

  function showCopiedCue() {
    clearTimeout(cueTimer.current);
    setHasCopied(true);
    cueTimer.current = setTimeout(() => setHasCopied(false), COPIED_CUE_DURATION);
  }

  async function handleClick() {
    // Inside the try, not before it. A synchronous throw out here (a Permissions-Policy violation in
    // a sandboxed embed can surface as one on property access) would escape into an unawaited async
    // handler, become an unhandled rejection, and leave the button looking live but doing nothing.
    try {
      const availability = availabilityOverride ?? detectClipboardAvailability();

      if (availability === 'unavailable') {
        setIsPopoverOpen(true);
        return;
      }

      await navigator.clipboard.writeText(url);
      showCopiedCue();
    } catch (error) {
      // The capability check said yes and the write still failed. Rather than apologise, hand over
      // the same surface the unavailable branch would have shown, so the link is still gettable.
      setIsPopoverOpen(true);
      onCopyError?.(error);
    }
  }

  return (
    <>
      {/* Labelled rather than a bare glyph. On a line of its own there is nothing beside it to say
          what it copies, and `alignSelf` keeps it the width of its own text instead of stretching
          across the row it sits in. */}
      <Button
        ref={anchorRef}
        size="small"
        startIcon={<LinkIcon />}
        onClick={handleClick}
        data-action="copy-request-link"
        sx={{ alignSelf: 'flex-start' }}
      >
        {hasCopied ? 'COPIED' : 'COPY REQUEST LINK'}
      </Button>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack
          sx={(t: Theme) => ({
            gap: t.semantic.spacing.xs,
            padding: t.semantic.spacing.base,
            width: t.semantic.size['2xs'],
          })}
        >
          <Typography variant="body2" color="text.secondary" sx={{ textWrap: 'pretty' }}>
            Copy this link and send it to your customer yourself.
          </Typography>
          <TextField
            value={url}
            // Named, because `autoFocus` lands a screen reader here the moment the popover opens and
            // an unnamed read-only field announces as "edit, read only, blank". The sentence above
            // is a sibling, not a label, so it is not exposed as this field's name.
            label="Request link"
            fullWidth
            size="small"
            // Pre-selected on open, because this branch exists precisely for the case where the
            // page cannot put the link on the clipboard for them. Scrolled back to the start
            // afterwards: selecting leaves the caret at the end, which scrolls a long URL so that
            // only its trailing identifier shows and the reader cannot tell what they are copying.
            autoFocus
            onFocus={(event) => {
              event.target.select();
              event.target.scrollLeft = 0;
            }}
            InputProps={{ readOnly: true }}
          />
        </Stack>
      </Popover>
    </>
  );
}
